import React, { forwardRef, useState, useEffect } from "react";
import { Row, Col } from "antd";
import { FormattedMessage } from "react-intl";
import DynamicItem from "../../../components/forms/DynamicItem";
import Date from "../../../components/tables/Date";
import EndWorkReason from "../../../components/tables/EndWorkReason";
import Surface from "../../../components/layouts/Surface";
import FormHeader from "../../../components/forms/FormHeader";
import { ReactComponent as RelativesImg } from "../../../assets/no-relatives.svg";
import { useSelector } from "react-redux";
import Moment from "moment";
import { extendMoment } from "moment-range";

const moment = extendMoment(Moment);

const WorkPeriods = forwardRef((props, ref) => {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [mode, setMode] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const activeEmployee = useSelector(
    (state) => state.employeeDetails.activeEmployee
  );
  const changes = useSelector(
    (state) => state.employeeDetails.changedItems.values
  );

  useEffect(() => {
    if (mode === "new") {
      setActiveItem(null);
      setStart(null);
      setEnd(null);
    }
  }, [mode]);

  const disabledNewStartDate = (current) => {
    let overlap = false;
    const mergedValues = { ...activeEmployee, ...changes };
    if (mergedValues.work_periods && mergedValues.work_periods.length > 0) {
      mergedValues.work_periods.forEach((item, i) => {
        if (activeItem && activeItem.id === item.id) {
          return;
        }
        const itemEnd = item.end || moment().add(100, "years");
        if (
          current.within(
            moment.range(
              moment(item.start).startOf("day"),
              moment(itemEnd).clone().endOf("day")
            )
          )
        ) {
          overlap = true;
        }
      });
    }
    return overlap || (end && current > moment(end));
  };

  const disabledEndDate = (current) => {
    let overlap = false;
    const mergedValues = { ...activeEmployee, ...changes };
    mergedValues.work_periods.forEach((item, i) => {
      if (!activeItem || (item.end && item.id !== activeItem.id)) {
        const range1 = moment.range(item.start, item.end);
        const range2 = moment.range(start, current);
        if (range1.overlaps(range2, { adjacent: true })) {
          overlap = true;
        }
      }
    });

    let beforeCurrentStart = start && current < moment(start);
    return overlap || beforeCurrentStart;
  };

  const hasOpenedPeriod = () => {
    const mergedValues = { ...activeEmployee, ...changes };
    if (!mergedValues.work_periods) {
      return false;
    }
    return mergedValues.work_periods.find((p) => p.end == null) != null;
  };

  const endDateRequired = () => {
    const mergedValues = { ...activeEmployee, ...changes };
    if (!mergedValues.work_periods) {
      return false;
    }
    if (!start) {
      return false;
    }
    return activeItem
      ? mergedValues.work_periods
          .filter((i) => i.id !== activeItem.id)
          .find((p) => moment(p.start).isAfter(moment(start))) != null
      : mergedValues.work_periods.find((p) =>
          moment(p.start).isAfter(moment(start))
        ) != null;
  };

  const onFormChange = (all) => {
    if (all) {
      setStart(all["work_periods.start"]);
      setEnd(all["work_periods.end"]);
    }
  };

  const setFormMode = (mode) => {
    setMode(mode);
  };

  const setFormActiveItem = (item) => {
    setActiveItem(item);
    setStart(item.start);
    setEnd(item.end);
  };

  const onFinish = () => {
    setActiveItem(null);
    setStart(null);
    setEnd(null);
  };

  const tableItems = [
    {
      title: "start",
      type: "date",
      required: true,
      disabledDate: (current) => disabledNewStartDate(current),
      render: Date,
      columnProps: { format: "L" },
      defaultSortOrder: "descend",
      sorter: (a, b) => a.start.localeCompare(b.start),
    },
    {
      title: "end",
      type: "date",
      render: Date,
      required: endDateRequired(),
      disabled: start === null,
      disabledDate: disabledEndDate,
      columnProps: { format: "L" },
    },
  ];
  const resonIdItem = {
    title: "reason_id",
    type: "settingsDataItem",
    render: EndWorkReason,
    required: true,
    settingsataName: "system_tables/end_work_period_reason/",
  };
  const formItems = [...tableItems];
  tableItems.push(resonIdItem);
  if (end) {
    formItems.push(resonIdItem);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      <Surface>
        <FormHeader
          title={<FormattedMessage id="app.employees.workPeriods" />}
        />
        <Row>
          <Col span={24}>
            <DynamicItem
              hideAddButton={() => hasOpenedPeriod()}
              onFormChange={onFormChange}
              resource={"employees"}
              action={"editor"}
              emptyImg={<RelativesImg />}
              name="work_periods"
              category="employee"
              setFormActiveItem={setFormActiveItem}
              setActiveItem={setActiveItem}
              setFormMode={setFormMode}
              onFinish={onFinish}
              sorter={(w1, w2) => w1.start.localeCompare(w2.start)}
              addText={
                <FormattedMessage id="app.employees.startNewEmployment" />
              }
              titleText={
                <FormattedMessage
                  id={
                    mode === "edit"
                      ? "app.employees.editEmployment"
                      : "app.employees.startNewEmployment"
                  }
                />
              }
              tableItems={tableItems}
              formItems={formItems}
              disabled={
                (!activeEmployee.local_id_number &&
                  activeEmployee.employee_type_id !== 2) ||
                (!activeEmployee?.foreign?.passport_number &&
                  activeEmployee.employee_type_id === 2)
              }
            />
          </Col>
        </Row>
      </Surface>
    </div>
  );
});

export default WorkPeriods;
